import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { createPost, deleteApprovedPost } from "./blog/blogFirebase";
import { useBlogTask } from "./blog/useBlogTask";
import ConfirmModal from "./blog/ConfirmModal";

const db = getFirestore();

const AdminBlogReview = () => {
  const [pendingPosts, setPendingPosts]     = useState([]);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [creating, setCreating]             = useState(false);
  const [confirmModal, setConfirmModal]     = useState(null);
  const [deletingId, setDeletingId]         = useState(null);
  const navigate = useNavigate();
  const { taskMsg, startTask, stopTask }    = useBlogTask();

  const handleCreate = async () => {
    setCreating(true);
    const taskId = startTask();
    try {
      const result = await createPost({ taskId });
      navigate(`/blog/${result.data.newDocId}`);
    } catch (e) {
      console.error("createBlogPost error:", e);
      setCreating(false);
    } finally {
      stopTask();
    }
  };

  // Subscribe to pending posts
  useEffect(() => {
    const q = query(
      collection(db, "blog_posts_pending"),
      where("status", "in", ["pending_approval", "regenerating"]),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error("BlogReview pending snapshot error:", err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Subscribe to published posts (no where filter — blog_posts only holds approved posts)
  useEffect(() => {
    const q = query(
      collection(db, "blog_posts"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setPublishedPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error("BlogReview published snapshot error:", err);
    });
    return () => unsub();
  }, []);

  const openDeleteConfirm = (post) => {
    setConfirmModal({
      title: "Delete Published Post",
      message: `"${post.title}" will be permanently deleted from the live blog. This cannot be undone.`,
      confirmLabel: "🗑 Delete",
      confirmClass: "bg-red-600 hover:bg-red-700",
      onConfirm: () => doDeletePublished(post.id),
    });
  };

  const doDeletePublished = async (docId) => {
    setConfirmModal(null);
    setDeletingId(docId);
    try {
      await deleteApprovedPost({ docId });
    } catch (e) {
      console.error("deleteBlogPost error:", e);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-xl text-gray-500">Loading posts…</div>
      </div>
    );
  }

  return (
    <div className="relative">
      {creating && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
          <div className="text-center px-6">
            <div className="w-14 h-14 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-5" />
            <p className="text-gray-700 text-base font-semibold mb-2">✍️ Generating new blog post…</p>
            {taskMsg && (
              <p className="text-gray-600 text-sm mb-1">{taskMsg}</p>
            )}
            <p className="text-gray-500 text-sm">This may take a minute — please don't close this page.</p>
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* ── Pending section ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">AI Blog Posts — Pending Approval</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Generated twice a week (Tue &amp; Fri at 9am PT). Click a post to review.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            ✍️ Create Now
          </button>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            pendingPosts.length > 0 ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"
          }`}>
            {pendingPosts.length === 0 ? "✓ All clear" : `${pendingPosts.length} pending`}
          </span>
        </div>
      </div>

      {pendingPosts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200 mb-10">
          <div className="text-5xl mb-4">📭</div>
          <p className="text-gray-500 text-lg">No posts pending review.</p>
          <p className="text-gray-400 text-sm mt-1">New posts are generated every Tuesday and Friday at 9am PT.</p>
        </div>
      )}

      {pendingPosts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100 mb-10">
          {pendingPosts.map(post => (
            <button
              key={post.id}
              onClick={() => navigate(`/blog/${post.id}`)}
              className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex items-center gap-3 group"
            >
              <span className="text-2xl shrink-0">{post.emoji || "📝"}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                  {post.title}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {post.date} · {post.readTime}
                  {post.status === "regenerating" && (
                    <span className="ml-2 text-yellow-600 font-medium">⏳ Regenerating…</span>
                  )}
                </p>
              </div>
              {post.image && (
                <img
                  src={post.image}
                  alt=""
                  className="w-16 h-10 object-cover rounded shrink-0 opacity-80"
                />
              )}
              <span className="text-gray-300 group-hover:text-gray-500 shrink-0">›</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Published section ────────────────────────────────────────────── */}
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-800">Published Posts</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Live on the blog. Delete permanently removes the post and its image.
        </p>
      </div>

      {publishedPosts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-5xl mb-4">📄</div>
          <p className="text-gray-500 text-lg">No published posts yet.</p>
        </div>
      )}

      {publishedPosts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {publishedPosts.map(post => (
            <div
              key={post.id}
              className="px-5 py-4 flex items-center gap-3"
            >
              <span className="text-2xl shrink-0">{post.emoji || "📝"}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{post.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{post.date} · {post.readTime}</p>
              </div>
              {post.image && (
                <img
                  src={post.image}
                  alt=""
                  className="w-16 h-10 object-cover rounded shrink-0 opacity-80"
                />
              )}
              <button
                onClick={() => openDeleteConfirm(post)}
                disabled={deletingId === post.id}
                className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Delete this post"
              >
                {deletingId === post.id ? "Deleting…" : "🗑 Delete"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlogReview;
